import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import ClassEditModal from './ClassEditModal';
import EnrollStudentModal from './EnrollStudentModal';
import JournalView from '../journal/JournalView';
import KindergartenView from '../journal/KindergartenView';

const HUE_PALETTE = [155, 175, 140, 195, 130, 165, 185, 145, 110, 200];
const classHue = (id) => HUE_PALETTE[(Number(id) || 0) % HUE_PALETTE.length];

function classDisplayName(c) {
  if (c.name) return c.name;
  if (c.stage === 'primary' && c.number !== null && c.stream) {
    return `${c.number} «${c.stream}»`;
  }
  return c.code;
}

function fmtDateRu(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
function age(iso) {
  if (!iso) return null;
  const b = new Date(iso); const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a;
}
// «1 год», «3 года», «5 лет» — колонка «Возраст» в списке учеников.
function ageLabel(iso) {
  const a = age(iso);
  if (a === null) return '—';
  const m10 = a % 10, m100 = a % 100;
  const word = (m10 === 1 && m100 !== 11) ? 'год'
    : (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) ? 'года' : 'лет';
  return `${a} ${word}`;
}
// Точный возраст с месяцами — в тултипе: для сада «4 года 11 мес.»
// информативнее целых лет.
function ageDetail(iso) {
  if (!iso) return '';
  const b = new Date(iso); const t = new Date();
  let months = (t.getFullYear() - b.getFullYear()) * 12 + (t.getMonth() - b.getMonth());
  if (t.getDate() < b.getDate()) months--;
  const m = ((months % 12) + 12) % 12;
  return ageLabel(iso) + (m > 0 ? ` ${m} мес.` : '');
}
function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
}

// Полный набор вкладок. Часть скрывается в зависимости от stage класса —
// у детского сада нет ни электронного дневника (вместо него Посещаемость),
// ни расписания. У школы — наоборот, дневник вместо посещаемости.
const ALL_TABS = [
  { id: 'students',   label: 'Ученики',                stages: ['primary','kindergarten'] },
  { id: 'journal',    label: 'Электронный дневник',    stages: ['primary'] },
  { id: 'attendance', label: 'Посещаемость',           stages: ['kindergarten'] },
  { id: 'schedule',   label: 'Расписание',             stages: ['primary'],            soon: true },
  { id: 'finance',    label: 'Финансы',                stages: ['primary','kindergarten'], soon: true },
  { id: 'events',     label: 'События',                stages: ['primary','kindergarten'], soon: true },
];

export default function ClassDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite = can('classes', 'write');

  const [cls, setCls]         = useState(null);
  const [years, setYears]     = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('students');
  const [editing, setEditing] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const reload = () => {
    setLoading(true); setError(null);
    Promise.all([
      api.get('/api/classes/' + id),
      api.get('/api/classes/' + id + '/students'),
      api.get('/api/years_study'),
    ]).then(([c, st, y]) => {
      setCls(c.class);
      setStudents(st.rows || []);
      setYears((y.rows || []).filter((yy) => !yy.archive));
    }).catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [id]);

  const reloadStudents = () => {
    api.get('/api/classes/' + id + '/students').then((res) => {
      setStudents(res.rows || []);
      // Также обновим enrolled в cls — чтобы счётчик в hero подтянулся
      api.get('/api/classes/' + id).then((r) => setCls(r.class)).catch(() => {});
    }).catch(() => {});
  };

  if (loading) return <div className="ref-empty" style={{ padding: 60 }}>Загрузка…</div>;
  if (error)   return <div style={{ padding: 32, color: 'var(--red-fg)' }}>Ошибка: {error}</div>;
  if (!cls)    return null;

  // Список вкладок, актуальных для текущей ступени.
  const tabs = ALL_TABS.filter((t) => t.stages.includes(cls.stage));
  // Если URL/state хранит вкладку, которой нет в этой ступени — проваливаемся
  // на первую доступную (обычно «Ученики»).
  const activeTab = tabs.some((t) => t.id === tab) ? tab : tabs[0]?.id;

  const hue = classHue(cls.id);
  const tone = `oklch(0.54 0.12 ${hue})`;
  const toneSoft = `oklch(0.96 0.04 ${hue})`;
  const toneSoft2 = `oklch(0.88 0.06 ${hue})`;
  const cap = cls.capacity || 0;
  const enrolled = cls.enrolled || 0;
  const fillPct = cap > 0 ? Math.round(enrolled / cap * 100) : 0;
  const free = Math.max(0, cap - enrolled);

  return (
    <>
      <Topbar
        breadcrumb={[
          { label: 'Обучение', to: '/classes' },
          { label: 'Классы',   to: '/classes?year=' + cls.yearId },
          cls.code,
        ]}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="cd-hero">
        <div className="cd-hero__left">
          <div className="cd-hero__badge" style={{ background: toneSoft, color: tone, borderColor: toneSoft2 }}>
            {cls.code}
          </div>
          <div>
            <div className="cd-hero__crumb">
              {cls.stage === 'kindergarten' ? 'Детский сад' : 'Начальная школа'}
              {cls.ageRange ? <> · {cls.ageRange}</> : null}
            </div>
            <h1 className="cd-hero__title">{classDisplayName(cls)}</h1>
            <div className="cd-hero__meta">
              {cls.room && (<><span><Icon name="badge" size={12} /> {cls.room}</span><span className="cd-hero__dot">·</span></>)}
              <span><Icon name="calendar" size={12} /> {cls.yearName} уч. год</span>
            </div>
          </div>
        </div>
        <div className="cd-hero__right">
          <div className="cd-hero__statpill" style={{ minWidth: 320, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div className="cd-hero__stat">
              <div className="cd-hero__statLabel">Учеников</div>
              <div className="cd-hero__statValue">{enrolled}<span className="muted">{cap > 0 ? '/' + cap : ''}</span></div>
            </div>
            <div className="cd-hero__stat">
              <div className="cd-hero__statLabel">Свободных мест</div>
              <div className="cd-hero__statValue">{cap > 0 ? free : '—'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="cd-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={'cd-tab' + (activeTab === t.id ? ' is-active' : '')}
            onClick={() => setTab(t.id)}
          >
            <span>{t.label}</span>
            {t.id === 'students' && <span className="cd-tab__count">{enrolled}</span>}
            {t.soon && <span className="cd-tab__count" title="В разработке">скоро</span>}
          </button>
        ))}

        <div style={{ flex: 1 }} />
        {canWrite && (
          <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)} style={{ marginRight: 12, alignSelf: 'center' }}>
            <Icon name="edit" size={13} /> Редактировать
          </button>
        )}
      </div>

      <div className="cd-body">
        <aside className="cd-side">
          <section className="cd-block">
            <header className="cd-block__head"><span>Классный руководитель</span></header>
            {cls.teacherName ? (
              <>
                <div className="cd-teacher">
                  <div className="cd-teacher__avatar" style={{ background: toneSoft, color: tone }}>
                    {initials(cls.teacherName)}
                  </div>
                  <div className="cd-teacher__text">
                    <div className="cd-teacher__name">{cls.teacherName}</div>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--fg-faint)', fontSize: 12.5 }}>Не назначен</div>
            )}
          </section>

          {cls.assistantName && (
            <section className="cd-block">
              <header className="cd-block__head"><span>{cls.stage === 'kindergarten' ? 'Помощник' : 'Ассистент'}</span></header>
              <div className="cd-teacher">
                <div className="cd-teacher__avatar" style={{ background: 'var(--panel-2)', color: 'var(--fg-muted)' }}>
                  {initials(cls.assistantName)}
                </div>
                <div className="cd-teacher__text">
                  <div className="cd-teacher__name">{cls.assistantName}</div>
                </div>
              </div>
            </section>
          )}

          <section className="cd-block">
            <header className="cd-block__head">
              <span>Наполняемость</span>
              {cap > 0 && <span className="cd-block__headRight">{fillPct}%</span>}
            </header>
            <div className="cd-fill">
              <div className="cd-fill__num"><b>{enrolled}</b><span className="muted"> / {cap || '—'}</span></div>
              {cap > 0 && (
                <div className="cd-bar">
                  <div className="cd-bar__fill" style={{ width: fillPct + '%', background: tone }} />
                </div>
              )}
              <div className="cd-fill__hint">
                {cap === 0 ? 'Без лимита' : free === 0 ? 'Класс укомплектован' : `${free} свободно`}
              </div>
            </div>
          </section>

          <section className="cd-block">
            <header className="cd-block__head"><span>Параметры</span></header>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5 }}>
              <div><span style={{ color: 'var(--fg-muted)' }}>Учебный год:</span> {cls.yearName}</div>
              <div><span style={{ color: 'var(--fg-muted)' }}>Кабинет:</span> {cls.room || '—'}</div>
              <div><span style={{ color: 'var(--fg-muted)' }}>Возраст:</span> {cls.ageRange || '—'}</div>
              <div><span style={{ color: 'var(--fg-muted)' }}>Код:</span> <span className="mono">{cls.code}</span></div>
              {cls.stage === 'primary' && (
                <div><span style={{ color: 'var(--fg-muted)' }}>Класс/параллель:</span> {cls.number}{cls.stream}</div>
              )}
            </div>
          </section>
        </aside>

        <main className="cd-main">
          {activeTab === 'students' && (
            <StudentsTab
              classId={cls.id}
              students={students}
              canWrite={canWrite}
              onOpenStudent={(sid) => navigate('/students?student=' + sid)}
              onEnroll={() => setEnrolling(true)}
              onChanged={reloadStudents}
            />
          )}
          {activeTab === 'journal' && (
            <div className="cd-journal">
              <JournalView classId={cls.id} />
            </div>
          )}
          {activeTab === 'attendance' && (
            <div className="cd-journal">
              <KindergartenView classId={cls.id} />
            </div>
          )}
          {activeTab && !['students', 'journal', 'attendance'].includes(activeTab) && (
            <PlaceholderTab title={tabs.find((t) => t.id === activeTab)?.label || ''} />
          )}
        </main>
      </div>

      {editing && (
        <ClassEditModal
          mode="edit"
          cls={cls}
          yearId={cls.yearId}
          years={years}
          onClose={() => setEditing(false)}
          onSaved={(c) => { setCls(c); }}
          onDeleted={() => navigate('/classes')}
        />
      )}

      {enrolling && (
        <EnrollStudentModal
          cls={cls}
          onClose={() => setEnrolling(false)}
          onEnrolled={reloadStudents}
        />
      )}
    </>
  );
}

function StudentsTab({ classId, students, canWrite, onOpenStudent, onEnroll, onChanged }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return students;
    return students.filter((st) => {
      const blob = (st.fullName + ' ' + (st.parent?.name || '') + ' ' + (st.parent?.phone || '')).toLowerCase();
      return blob.includes(s);
    });
  }, [students, q]);

  return (
    <div className="cd-students">
      <div className="cd-toolbar">
        <div className="cls-toolbar__search" style={{ maxWidth: 360 }}>
          <Icon name="search" size={13} />
          <input
            placeholder="Найти ученика или родителя…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && <button onClick={() => setQ('')}><Icon name="x" size={11} /></button>}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>
          {filtered.length === students.length ? `${students.length} учеников` : `${filtered.length} из ${students.length}`}
        </span>
        {canWrite && (
          <button className="btn btn--primary btn--sm" onClick={onEnroll}>
            <Icon name="plus" size={13} /> Зачислить ученика
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="ref-empty" style={{ padding: 40 }}>
          {students.length === 0 ? 'В классе нет учеников.' : 'Ничего не найдено.'}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ученик</th>
                <th style={{ width: 110 }}>Дата рожд.</th>
                <th style={{ width: 90 }}>Возраст</th>
                <th style={{ width: 200 }}>Период обучения</th>
                <th style={{ width: 240 }}>Родитель</th>
                <th style={{ width: 110 }}>Статус</th>
                {canWrite && <th style={{ width: 80 }} />}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <EnrollmentRow
                  key={s.enrollmentId}
                  classId={classId}
                  student={s}
                  canWrite={canWrite}
                  onOpenStudent={onOpenStudent}
                  onChanged={onChanged}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EnrollmentRow({ classId, student: s, canWrite, onOpenStudent, onChanged }) {
  const [editing, setEditing]   = useState(false);
  const [dateStart, setDateStart] = useState(s.enrolledFrom || '');
  const [dateEnd,   setDateEnd]   = useState(s.enrolledTo   || '');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  const startEdit = (e) => {
    e.stopPropagation();
    setDateStart(s.enrolledFrom || '');
    setDateEnd(s.enrolledTo || '');
    setError(null);
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setError(null); };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.put(`/api/classes/${classId}/enrollments/${s.enrollmentId}`, {
        dateStart, dateEnd,
      });
      onChanged?.();
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
    } finally { setSaving(false); }
  };

  const remove = async (e) => {
    e.stopPropagation();
    if (!confirm(`Снять «${s.fullName}» с зачисления в классе?`)) return;
    setSaving(true); setError(null);
    try {
      await api.delete(`/api/classes/${classId}/enrollments/${s.enrollmentId}`);
      onChanged?.();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : (err.message || 'Не удалось'));
      setSaving(false);
    }
  };

  return (
    <>
      <tr
        onClick={() => onOpenStudent(s.id)}
        style={{ cursor: 'pointer' }}
        className={s.isPast ? 'enrollment-row--past' : ''}
      >
        <td>
          {/* Настоящая ссылка (а не только onClick строки) — чтобы работал
              ПКМ → «Открыть в новой вкладке» и ctrl/средний клик. Обычный
              левый клик перехватывает Link и навигирует внутри SPA. */}
          <Link
            to={'/students?student=' + s.id}
            className="cell-student"
            style={{ color: 'inherit', textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cell-student__avatar"
                 style={{ background: s.sex === 'female' ? 'oklch(0.94 0.05 340)' : 'oklch(0.94 0.05 230)',
                          color: s.sex === 'female' ? 'oklch(0.45 0.12 340)' : 'oklch(0.45 0.12 230)' }}>
              {initials(s.fullName)}
            </div>
            <div className="cell-student__text">
              <span className="cell-student__name">{s.fullName}</span>
            </div>
          </Link>
        </td>
        <td className="muted mono">{fmtDateRu(s.birthdate)}</td>
        <td className="muted" title={ageDetail(s.birthdate)}>{ageLabel(s.birthdate)}</td>
        <td className="muted">
          <span className="mono" style={{ fontSize: 11.5 }}>
            {fmtDateRu(s.enrolledFrom)} — {fmtDateRu(s.enrolledTo)}
          </span>
          {s.isFuture && (
            <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--amber-fg)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              с {fmtDateRu(s.enrolledFrom)}
            </span>
          )}
          {s.isPast && (
            <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              выбыл
            </span>
          )}
        </td>
        <td>
          {s.parent ? (
            <div className="cell-parent">
              <div className="cell-parent__name">{s.parent.name}</div>
              {s.parent.phone && <div className="cell-parent__phone mono">{s.parent.phone}</div>}
            </div>
          ) : <span style={{ color: 'var(--fg-faint)' }}>—</span>}
        </td>
        <td>
          {s.status === 'active'
            ? <span className="sb sb--active">Активен</span>
            : <span className="sb sb--archived">Архив</span>}
        </td>
        {canWrite && (
          <td onClick={(e) => e.stopPropagation()}>
            <div className="ref-row-actions">
              <button onClick={startEdit} title="Изменить период"><Icon name="edit" size={14} /></button>
              <button className="is-danger" onClick={remove} title="Снять с зачисления" disabled={saving}>
                <Icon name="x" size={14} />
              </button>
            </div>
          </td>
        )}
      </tr>
      {editing && (
        <tr className="enrollment-edit-row">
          <td colSpan={canWrite ? 7 : 6}>
            <div className="enroll-row__form" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              <div className="enroll-row__field">
                <label>Дата начала</label>
                <input type="date" value={dateStart || ''} onChange={(e) => setDateStart(e.target.value)} />
              </div>
              <div className="enroll-row__field">
                <label>Дата окончания</label>
                <input type="date" value={dateEnd || ''} onChange={(e) => setDateEnd(e.target.value)} min={dateStart || undefined} />
              </div>
              <div className="enroll-row__formHint" style={{ color: error ? 'var(--red-fg)' : undefined }}>
                {error || 'Период должен быть в пределах действия договора ученика.'}
              </div>
              <div className="enroll-row__formActions">
                <button type="button" className="btn btn--sm" onClick={cancel} disabled={saving}>Отмена</button>
                <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={saving || !dateStart || !dateEnd}>
                  {saving ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PlaceholderTab({ title }) {
  return (
    <div className="cd-empty-block cd-empty-block--big">
      <div className="cd-empty-block__title">{title}</div>
      <div className="cd-empty-block__hint">Раздел в разработке. Появится, когда добавим соответствующую подсистему.</div>
    </div>
  );
}
